import { useState } from 'react';
import { useEffect } from 'react';
import { api } from '@/api/client';
import { useAuth } from '@/context/AuthContext';

export default function Organizations() {
  interface Org { id: string; name: string; slug: string; subdomain?: string | null; logo_url?: string | null; is_active: boolean; member_count: number; created_at?: string }
  const { orgId } = useAuth();
  const [orgs, setOrgs] = useState<Org[]>([]);
  useEffect(() => {
    api.get<Org[]>('/organizations').then(setOrgs).catch(() => {});
  }, [orgId]);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    const slug = newName.trim().toLowerCase()
      .replace(/[^a-z0-9а-яё]+/g, '-').replace(/^-+|-+$/g, '')
      .replace(/[а-яё]/g, (ch) => ({'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e','ё':'e','ж':'zh','з':'z','и':'i','й':'y','к':'k','л':'l','м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u','ф':'f','х':'h','ц':'c','ч':'ch','ш':'sh','щ':'sch','ъ':'','ы':'y','ь':'','э':'e','ю':'yu','я':'ya'}[ch] || ''));
    try {
      const created = await api.post<Org>('/organizations', { name: newName.trim(), slug });
      setOrgs((prev) => [...prev, created]);
      setNewName('');
      setShowAdd(false);
    } catch (err) {
      alert((err as Error).message || 'Ошибка создания');
    }
    setCreating(false);
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
            Организации
          </h1>
          <p className="text-sm text-foreground-500 mt-1">
            Управление организациями и доступом
          </p>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="text-xs flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Новая организация
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="bg-white rounded-xl border border-background-200 p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-3">Создать организацию</h3>
          <form onSubmit={handleCreate} className="flex gap-3">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Название организации"
              required
              className="flex-1 px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            <button
              type="submit"
              disabled={creating}
              className="px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
            >
              {creating ? <i className="ri-loader-4-line animate-spin" /> : 'Создать'}
            </button>
          </form>
        </div>
      )}

      {/* Organizations grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {orgs.map((org) => (
          <div key={org.id} className="bg-white rounded-xl border border-background-200 p-5 hover:border-primary-300 transition-colors">
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                <span className="text-primary-700 font-heading font-bold text-sm">{org.name.charAt(0)}</span>
              </div>
              <button className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer">
                <i className="ri-more-2-line" />
              </button>
            </div>
            <h3 className="text-base font-semibold text-foreground-900 mb-1">{org.name}</h3>
            <p className="text-xs text-foreground-400 mb-4">Создана {org.created_at ? formatDate(org.created_at) : '—'}</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-background-50 rounded-lg p-2.5 text-center">
                <p className="text-lg font-heading font-bold text-foreground-900">{org.member_count}</p>
                <p className="text-[10px] text-foreground-500">участников</p>
              </div>
              <div className="bg-background-50 rounded-lg p-2.5 text-center">
                <p className="text-lg font-heading font-bold text-foreground-900">{org.subdomain || org.slug}</p>
                <p className="text-[10px] text-foreground-500">поддомен лендингов</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}